import { useState, useEffect } from 'react';
import { Video, XCircle, Loader2, Unlink } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { zoomService, type VideoConnection, type VideoProvider } from '../../services/zoomService';
import { calendarService } from '../../services/calendarService';
import type { CalendarProvider } from '../../types/calendar';

interface ProviderOption {
  id: VideoProvider;
  name: string;
  iconSrc: string;
}

const ALL_PROVIDERS: ProviderOption[] = [
  { id: 'google_meet', name: 'Google Meet', iconSrc: '/google-meets-icon.svg' },
  { id: 'microsoft_teams', name: 'Microsoft Teams', iconSrc: '/microsoft-teams-icon.svg' },
  { id: 'zoom', name: 'Zoom', iconSrc: '/zoom-meetings-icon.svg' },
];

function getAvailableProviders(calendarProvider: CalendarProvider | null): ProviderOption[] {
  if (calendarProvider === 'google') {
    return ALL_PROVIDERS.filter(p => p.id === 'google_meet' || p.id === 'zoom');
  }
  if (calendarProvider === 'microsoft') {
    return ALL_PROVIDERS.filter(p => p.id === 'microsoft_teams' || p.id === 'zoom');
  }
  return [];
}

const STORAGE_KEYS: Record<string, string> = {
  'zoom-oauth': 'oauth-result-zoom',
  'google-meet-oauth': 'oauth-result-google-meet',
  'microsoft-teams-oauth': 'oauth-result-microsoft-teams',
};

export function MemberVideoSection() {
  const { user } = useAuth();
  const [salesMemberId, setSalesMemberId] = useState<string | null>(null);
  const [calendarProvider, setCalendarProvider] = useState<CalendarProvider | null>(null);
  const [videoConnection, setVideoConnection] = useState<VideoConnection | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    if (!user?.client_id || !user?.id) return;
    try {
      setLoading(true);
      const member = await calendarService.getMyLinkedSalesMember(user.client_id, user.id);
      if (!member) {
        setLoading(false);
        return;
      }
      setSalesMemberId(member.id);

      const [calConn, vidConn] = await Promise.all([
        calendarService.getCalendarConnectionForMember(user.client_id, member.id),
        zoomService.getVideoConnectionForMember(user.client_id, member.id),
      ]);

      if (calConn) setCalendarProvider(calConn.provider as CalendarProvider);
      setVideoConnection(vidConn);
    } catch (err) {
      console.error('Error loading video data:', err);
      setError('Failed to load video connection');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [user]);

  const openOAuthPopup = (authUrl: string, messageType: string, providerName: string) => {
    setConnecting(true);
    setError(null);

    const storageKey = STORAGE_KEYS[messageType];
    if (storageKey) localStorage.removeItem(storageKey);

    try {
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        authUrl,
        `${messageType}-popup`,
        `width=${width},height=${height},left=${left},top=${top}`
      );

      let timeoutId: number | null = null;
      let pollId: number | null = null;

      const cleanup = () => {
        if (timeoutId) clearTimeout(timeoutId);
        if (pollId) clearInterval(pollId);
      };

      pollId = window.setInterval(() => {
        if (popup && popup.closed) {
          const raw = storageKey ? localStorage.getItem(storageKey) : null;
          if (!raw) {
            cleanup();
            setConnecting(false);
            return;
          }
        }

        if (!storageKey) return;
        const raw = localStorage.getItem(storageKey);
        if (!raw) return;

        cleanup();
        localStorage.removeItem(storageKey);

        try {
          const data = JSON.parse(raw);
          if (data.success) {
            loadData();
          } else {
            setError(data.error || `Failed to connect ${providerName}`);
          }
        } catch {
          setError(`Failed to connect ${providerName}`);
        }
        setConnecting(false);
      }, 500);

      timeoutId = window.setTimeout(() => {
        cleanup();
        setConnecting(false);
        setError('Connection timed out. Please try again.');
      }, 120000);
    } catch (err) {
      console.error(`Error initiating ${providerName} OAuth:`, err);
      setError('Failed to start connection process.');
      setConnecting(false);
    }
  };

  const handleConnect = async (provider: VideoProvider) => {
    if (!user?.client_id || !salesMemberId) return;

    try {
      if (provider === 'zoom') {
        const authUrl = await zoomService.initiateZoomOAuth(user.client_id, user.email || '', salesMemberId);
        openOAuthPopup(authUrl, 'zoom-oauth', 'Zoom');
      } else if (provider === 'google_meet') {
        const authUrl = await zoomService.initiateGoogleMeetOAuth(user.client_id, user.email || '', salesMemberId);
        openOAuthPopup(authUrl, 'google-meet-oauth', 'Google Meet');
      } else if (provider === 'microsoft_teams') {
        const authUrl = await zoomService.initiateMicrosoftTeamsOAuth(user.client_id, user.email || '', salesMemberId);
        openOAuthPopup(authUrl, 'microsoft-teams-oauth', 'Microsoft Teams');
      }
    } catch (err) {
      console.error('Error initiating video OAuth:', err);
      setError('Failed to start connection process.');
    }
  };

  const handleDisconnect = async () => {
    if (!user?.client_id || !salesMemberId || !videoConnection) return;
    if (!confirm('Are you sure you want to disconnect your video provider?')) return;

    try {
      setDisconnecting(true);
      let result;
      if (videoConnection.provider === 'google_meet') {
        result = await zoomService.disconnectGoogleMeet(user.client_id, salesMemberId);
      } else if (videoConnection.provider === 'microsoft_teams') {
        result = await zoomService.disconnectMicrosoftTeams(user.client_id, salesMemberId);
      } else {
        result = await zoomService.disconnectZoom(user.client_id, salesMemberId);
      }
      if (result.success) {
        setVideoConnection(null);
      } else {
        setError('Failed to disconnect video provider');
      }
    } catch (err) {
      console.error('Error disconnecting video:', err);
      setError('Failed to disconnect');
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-[#1875FF] animate-spin" />
        </div>
      </div>
    );
  }

  if (!salesMemberId) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="text-center py-8">
          <Video className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">Connect your calendar first to enable video call settings.</p>
        </div>
      </div>
    );
  }

  if (!calendarProvider) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="text-center py-8">
          <Video className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">Connect your calendar first to enable video call settings.</p>
        </div>
      </div>
    );
  }

  const availableProviders = getAvailableProviders(calendarProvider);

  if (videoConnection) {
    const matchedProvider = ALL_PROVIDERS.find(p => p.id === videoConnection.provider);
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        {error && (
          <div className="p-3 mb-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {matchedProvider && (
              <div className="w-10 h-10 rounded-lg bg-gray-50 border border-gray-200 flex items-center justify-center">
                <img src={matchedProvider.iconSrc} alt={matchedProvider.name} className="w-6 h-6" />
              </div>
            )}
            <div>
              <p className="text-sm font-bold text-gray-900">{matchedProvider?.name || videoConnection.provider}</p>
              <p className="text-xs text-gray-500">Video calls will use this provider</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1.5 px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
              <div className="w-1.5 h-1.5 bg-green-500 rounded-full" />
              Connected
            </span>
            <button
              onClick={handleDisconnect}
              disabled={disconnecting}
              className="p-2 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
              title="Disconnect"
            >
              {disconnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
      {error && (
        <div className="p-3 mb-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      <p className="text-sm text-gray-600 mb-4">Choose your preferred video conferencing platform for meetings.</p>
      <div className="flex items-center gap-2 mb-4">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 text-red-600 rounded-full text-sm font-medium">
          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          <span>Not connected</span>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {availableProviders.map((provider) => (
          <button
            key={provider.id}
            onClick={() => handleConnect(provider.id)}
            disabled={connecting}
            className="flex items-center gap-3 p-4 border border-gray-200 rounded-xl hover:border-[#1875FF] hover:bg-blue-50/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="w-10 h-10 rounded-lg bg-gray-50 border border-gray-200 flex items-center justify-center">
              <img src={provider.iconSrc} alt={provider.name} className="w-6 h-6" />
            </div>
            <span className="text-sm font-medium text-gray-900">{provider.name}</span>
            {connecting && <Loader2 className="w-4 h-4 animate-spin ml-auto text-[#1875FF]" />}
          </button>
        ))}
      </div>
    </div>
  );
}
